import { createAssetGetter } from '@labradorsports/assets';
import { footballlab } from '@labradorsports/constants';

export const Logo = createAssetGetter('img/football/logo.svg');

export const SiteConfig = {
    ...footballlab.SiteConfig,
    AppStoreUrl: 'https://apps.apple.com/il/app/hoops-lab/id1576985969',
    GooglePlayUrl: 'https://play.google.com/store/apps/details?id=com.labradorsports.hoopslab&hl=en_US&gl=US',
    Logo,
    PlaybookDescription:
        'Free play designer to create and share your basketball playbook. Create offense, defense and clearing plays. Add moves, passes, and comments to help guide your players.',
};

export const GoogleAnalyticsID = 'G-7F6KTZDQZ1';

export const Sports = footballlab.Sports;

export const SportsNames = {
    [Sports.HighSchoolFootball]: 'High-School Football',
    [Sports.CollegeFootball]: 'College Football',
};

export const PlayerPositions = {
    QUARTERBACK: 'QUARTERBACK',
    WIDE_RECEIVER: 'WIDE_RECEIVER',
    RUNNING_BACK: 'RUNNING_BACK',
    TIGHT_END: 'TIGHT_END',
    OFFENSIVE_LINEMAN: 'OFFENSIVE_LINEMAN',
    DEFENSIVE_LINEMAN: 'DEFENSIVE_LINEMAN',
    LINEBACKER: 'LINEBACKER',
    CENTER: 'CENTER',
    OFFENSIVE_TACKLE: 'OFFENSIVE_TACKLE',
    OFFENSIVE_GUARD: 'OFFENSIVE_GUARD',
    CORNERBACK: 'CORNERBACK',
    SAFETY: 'SAFETY',
    DEFENSIVE_TACKLE: 'DEFENSIVE_TACKLE',
    DEFENSIVE_END: 'DEFENSIVE_END',
    OUTSIDE_LINEBACKER: 'OUTSIDE_LINEBACKER',
    MIDDLE_LINEBACKER: 'MIDDLE_LINEBACKER',
    KICKER: 'KICKER',
    PUNTER: 'PUNTER',
    DEFENSIVE_BACK: 'DEFENSIVE_BACK',
};

export const PlayerPositionNames = {
    [PlayerPositions.QUARTERBACK]: 'Quarterback',
    [PlayerPositions.WIDE_RECEIVER]: 'Wide Receiver',
    [PlayerPositions.RUNNING_BACK]: 'Running Back',
    [PlayerPositions.OFFENSIVE_LINEMAN]: 'Offensive Lineman',
    [PlayerPositions.DEFENSIVE_LINEMAN]: 'Defensive Lineman',
    [PlayerPositions.LINEBACKER]: 'Linebacker',
    [PlayerPositions.TIGHT_END]: 'Tight End',
    [PlayerPositions.CENTER]: 'Center',
    [PlayerPositions.OFFENSIVE_TACKLE]: 'Offensive Tackle',
    [PlayerPositions.OFFENSIVE_GUARD]: 'Offensive Guard',
    [PlayerPositions.SAFETY]: 'Safety',
    [PlayerPositions.CORNERBACK]: 'Cornerback',
    [PlayerPositions.DEFENSIVE_END]: 'Defensive End',
    [PlayerPositions.DEFENSIVE_TACKLE]: 'Defensive Tackle',
    [PlayerPositions.OUTSIDE_LINEBACKER]: 'Outside Linebacker',
    [PlayerPositions.MIDDLE_LINEBACKER]: 'Middle Linebacker',
    [PlayerPositions.KICKER]: 'Kicker',
    [PlayerPositions.PUNTER]: 'Punter',
    [PlayerPositions.DEFENSIVE_BACK]: 'Defensive Back',
};

export const SportPositions = {
    [Sports.HighSchoolFootball]: Object.values(PlayerPositions),
    [Sports.CollegeFootball]: Object.values(PlayerPositions),
};

export const EvaluationCriteria = {
    SPEED: 'SPEED',
    AGILITY: 'AGILITY',
    STRENGTH: 'STRENGTH',
    ENDURANCE: 'ENDURANCE',
    THROWING_ACCURACY: 'THROWING_ACCURACY',
    ARM_STRENGTH: 'ARM_STRENGTH',
    CATCHING: 'CATCHING',
    TACKLING: 'TACKLING',
    BLOCKING: 'BLOCKING',
    POSITION_TECHNIQUE: 'POSITION_TECHNIQUE',
    FOOTBALL_IQ: 'FOOTBALL_IQ',
    BALL_SECURITY: 'BALL_SECURITY',
    LEADERSHIP: 'LEADERSHIP',
    COMMUNICATION: 'COMMUNICATION',
    EFFORT: 'EFFORT',
};

export const PlayerRatingSections = [
    {
        id: 'athleticism',
        sectionName: 'Athleticism',
        criteria: [
            { id: EvaluationCriteria.SPEED, criteriaLabel: 'Speed' },
            { id: EvaluationCriteria.AGILITY, criteriaLabel: 'Agility' },
            { id: EvaluationCriteria.STRENGTH, criteriaLabel: 'Strength' },
            { id: EvaluationCriteria.ENDURANCE, criteriaLabel: 'Endurance' },
        ],
    },
    {
        id: 'skills',
        sectionName: 'Skills',
        criteria: [
            { id: EvaluationCriteria.THROWING_ACCURACY, criteriaLabel: 'Throwing Accuracy' },
            { id: EvaluationCriteria.ARM_STRENGTH, criteriaLabel: 'Arm Strength' },
            { id: EvaluationCriteria.CATCHING, criteriaLabel: 'Catching' },
            { id: EvaluationCriteria.TACKLING, criteriaLabel: 'Tackling' },
            { id: EvaluationCriteria.BLOCKING, criteriaLabel: 'Blocking' },
            { id: EvaluationCriteria.POSITION_TECHNIQUE, criteriaLabel: 'Position-specific Technique' },
        ],
    },
    {
        id: 'awareness',
        sectionName: 'Awareness',
        criteria: [
            { id: EvaluationCriteria.FOOTBALL_IQ, criteriaLabel: 'Football IQ' },
            { id: EvaluationCriteria.BALL_SECURITY, criteriaLabel: 'Ball Security' },
        ],
    },
    {
        id: 'intangibles',
        sectionName: 'Intangibles',
        criteria: [
            { id: EvaluationCriteria.LEADERSHIP, criteriaLabel: 'Leadership' },
            { id: EvaluationCriteria.COMMUNICATION, criteriaLabel: 'Communication' },
            { id: EvaluationCriteria.EFFORT, criteriaLabel: 'Effort' },
        ],
    },
];

const QuarterbackWeights = {
    [EvaluationCriteria.SPEED]: 7.5,
    [EvaluationCriteria.AGILITY]: 7.5,
    [EvaluationCriteria.STRENGTH]: 3.8,
    [EvaluationCriteria.ENDURANCE]: 3.8,
    [EvaluationCriteria.THROWING_ACCURACY]: 22.6,
    [EvaluationCriteria.ARM_STRENGTH]: 17.9,
    [EvaluationCriteria.CATCHING]: 3.8,
    [EvaluationCriteria.TACKLING]: 0,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 3.8,
    [EvaluationCriteria.FOOTBALL_IQ]: 9.4,
    [EvaluationCriteria.BALL_SECURITY]: 3.8,
    [EvaluationCriteria.LEADERSHIP]: 6.6,
    [EvaluationCriteria.COMMUNICATION]: 4.7,
    [EvaluationCriteria.EFFORT]: 4.7,
};

const WideReceiverWeights = {
    [EvaluationCriteria.SPEED]: 18,
    [EvaluationCriteria.AGILITY]: 15,
    [EvaluationCriteria.STRENGTH]: 4,
    [EvaluationCriteria.ENDURANCE]: 8,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 23,
    [EvaluationCriteria.TACKLING]: 0,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 8,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 8,
    [EvaluationCriteria.LEADERSHIP]: 3,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 6,
};

const RunningBackWeights = {
    [EvaluationCriteria.SPEED]: 14.2,
    [EvaluationCriteria.AGILITY]: 14.2,
    [EvaluationCriteria.STRENGTH]: 6.6,
    [EvaluationCriteria.ENDURANCE]: 4.7,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 12.3,
    [EvaluationCriteria.TACKLING]: 8.5,
    [EvaluationCriteria.BLOCKING]: 4.7,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 4.7,
    [EvaluationCriteria.FOOTBALL_IQ]: 4.7,
    [EvaluationCriteria.BALL_SECURITY]: 12.3,
    [EvaluationCriteria.LEADERSHIP]: 1.9,
    [EvaluationCriteria.COMMUNICATION]: 1.9,
    [EvaluationCriteria.EFFORT]: 9.4,
};

const TightEndWeights = {
    [EvaluationCriteria.SPEED]: 9.5,
    [EvaluationCriteria.AGILITY]: 7.6,
    [EvaluationCriteria.STRENGTH]: 11.4,
    [EvaluationCriteria.ENDURANCE]: 7.6,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 19,
    [EvaluationCriteria.TACKLING]: 5.7,
    [EvaluationCriteria.BLOCKING]: 15.2,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 7.6,
    [EvaluationCriteria.FOOTBALL_IQ]: 4.8,
    [EvaluationCriteria.BALL_SECURITY]: 3.8,
    [EvaluationCriteria.LEADERSHIP]: 1.9,
    [EvaluationCriteria.COMMUNICATION]: 1,
    [EvaluationCriteria.EFFORT]: 4.8,
};

const CenterWeights = {
    [EvaluationCriteria.SPEED]: 2.9,
    [EvaluationCriteria.AGILITY]: 4.8,
    [EvaluationCriteria.STRENGTH]: 17.1,
    [EvaluationCriteria.ENDURANCE]: 4.8,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 4.8,
    [EvaluationCriteria.BLOCKING]: 24.8,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 17.1,
    [EvaluationCriteria.FOOTBALL_IQ]: 6.7,
    [EvaluationCriteria.BALL_SECURITY]: 1.9,
    [EvaluationCriteria.LEADERSHIP]: 5.7,
    [EvaluationCriteria.COMMUNICATION]: 2.9,
    [EvaluationCriteria.EFFORT]: 6.7,
};

const OffensiveTackleWeights = {
    [EvaluationCriteria.SPEED]: 4,
    [EvaluationCriteria.AGILITY]: 6,
    [EvaluationCriteria.STRENGTH]: 20,
    [EvaluationCriteria.ENDURANCE]: 6,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 5,
    [EvaluationCriteria.BLOCKING]: 25,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 18,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 3,
    [EvaluationCriteria.LEADERSHIP]: 2,
    [EvaluationCriteria.COMMUNICATION]: 1,
    [EvaluationCriteria.EFFORT]: 5,
};

const OffensiveGuardWeights = {
    [EvaluationCriteria.SPEED]: 3,
    [EvaluationCriteria.AGILITY]: 5,
    [EvaluationCriteria.STRENGTH]: 22,
    [EvaluationCriteria.ENDURANCE]: 5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 4,
    [EvaluationCriteria.BLOCKING]: 26,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 18,
    [EvaluationCriteria.FOOTBALL_IQ]: 4,
    [EvaluationCriteria.BALL_SECURITY]: 3,
    [EvaluationCriteria.LEADERSHIP]: 2,
    [EvaluationCriteria.COMMUNICATION]: 1,
    [EvaluationCriteria.EFFORT]: 7,
};

const CornerbackWeights = {
    [EvaluationCriteria.SPEED]: 18,
    [EvaluationCriteria.AGILITY]: 18,
    [EvaluationCriteria.STRENGTH]: 4,
    [EvaluationCriteria.ENDURANCE]: 5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 12,
    [EvaluationCriteria.TACKLING]: 10,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 10,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 4,
    [EvaluationCriteria.LEADERSHIP]: 4,
    [EvaluationCriteria.COMMUNICATION]: 4,
    [EvaluationCriteria.EFFORT]: 6,
};

const SafetyWeights = {
    [EvaluationCriteria.SPEED]: 13,
    [EvaluationCriteria.AGILITY]: 13,
    [EvaluationCriteria.STRENGTH]: 5.7,
    [EvaluationCriteria.ENDURANCE]: 4.7,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 9.4,
    [EvaluationCriteria.TACKLING]: 13.2,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 11.3,
    [EvaluationCriteria.FOOTBALL_IQ]: 7.5,
    [EvaluationCriteria.BALL_SECURITY]: 3.8,
    [EvaluationCriteria.LEADERSHIP]: 5.7,
    [EvaluationCriteria.COMMUNICATION]: 4.7,
    [EvaluationCriteria.EFFORT]: 7.5,
};

const DefensiveTackleWeights = {
    [EvaluationCriteria.SPEED]: 4,
    [EvaluationCriteria.AGILITY]: 5,
    [EvaluationCriteria.STRENGTH]: 26,
    [EvaluationCriteria.ENDURANCE]: 4,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 25,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 16,
    [EvaluationCriteria.FOOTBALL_IQ]: 4,
    [EvaluationCriteria.BALL_SECURITY]: 3,
    [EvaluationCriteria.LEADERSHIP]: 2,
    [EvaluationCriteria.COMMUNICATION]: 1,
    [EvaluationCriteria.EFFORT]: 10,
};

const DefensiveEndWeights = {
    [EvaluationCriteria.SPEED]: 8,
    [EvaluationCriteria.AGILITY]: 8,
    [EvaluationCriteria.STRENGTH]: 20,
    [EvaluationCriteria.ENDURANCE]: 5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 20,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 16,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 4,
    [EvaluationCriteria.LEADERSHIP]: 3,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 9,
};

const OutsideLinebackerWeights = {
    [EvaluationCriteria.SPEED]: 9.2,
    [EvaluationCriteria.AGILITY]: 9.2,
    [EvaluationCriteria.STRENGTH]: 12.8,
    [EvaluationCriteria.ENDURANCE]: 4.6,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 3.7,
    [EvaluationCriteria.TACKLING]: 21.1,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 12.8,
    [EvaluationCriteria.FOOTBALL_IQ]: 4.6,
    [EvaluationCriteria.BALL_SECURITY]: 2.8,
    [EvaluationCriteria.LEADERSHIP]: 5.5,
    [EvaluationCriteria.COMMUNICATION]: 4.6,
    [EvaluationCriteria.EFFORT]: 9.2,
};

const MiddleLinebackerWeights = {
    [EvaluationCriteria.SPEED]: 7.3,
    [EvaluationCriteria.AGILITY]: 7.3,
    [EvaluationCriteria.STRENGTH]: 13.6,
    [EvaluationCriteria.ENDURANCE]: 4.5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 2.7,
    [EvaluationCriteria.TACKLING]: 22.7,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 10.9,
    [EvaluationCriteria.FOOTBALL_IQ]: 7.3,
    [EvaluationCriteria.BALL_SECURITY]: 2.7,
    [EvaluationCriteria.LEADERSHIP]: 6.4,
    [EvaluationCriteria.COMMUNICATION]: 5.5,
    [EvaluationCriteria.EFFORT]: 9,
};

const KickerWeights = {
    [EvaluationCriteria.SPEED]: 0,
    [EvaluationCriteria.AGILITY]: 4,
    [EvaluationCriteria.STRENGTH]: 4,
    [EvaluationCriteria.ENDURANCE]: 4,
    [EvaluationCriteria.THROWING_ACCURACY]: 8,
    [EvaluationCriteria.ARM_STRENGTH]: 20,
    [EvaluationCriteria.CATCHING]: 4,
    [EvaluationCriteria.TACKLING]: 0,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 30,
    [EvaluationCriteria.FOOTBALL_IQ]: 8,
    [EvaluationCriteria.BALL_SECURITY]: 4,
    [EvaluationCriteria.LEADERSHIP]: 2,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 10,
};

const PunterWeights = {
    [EvaluationCriteria.SPEED]: 0,
    [EvaluationCriteria.AGILITY]: 4,
    [EvaluationCriteria.STRENGTH]: 4,
    [EvaluationCriteria.ENDURANCE]: 4,
    [EvaluationCriteria.THROWING_ACCURACY]: 8,
    [EvaluationCriteria.ARM_STRENGTH]: 20,
    [EvaluationCriteria.CATCHING]: 4,
    [EvaluationCriteria.TACKLING]: 0,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 30,
    [EvaluationCriteria.FOOTBALL_IQ]: 8,
    [EvaluationCriteria.BALL_SECURITY]: 4,
    [EvaluationCriteria.LEADERSHIP]: 2,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 10,
};

const DefensiveBackWeights = {
    [EvaluationCriteria.SPEED]: 17,
    [EvaluationCriteria.AGILITY]: 17,
    [EvaluationCriteria.STRENGTH]: 5,
    [EvaluationCriteria.ENDURANCE]: 5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 13,
    [EvaluationCriteria.TACKLING]: 13,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 10,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 3,
    [EvaluationCriteria.LEADERSHIP]: 4,
    [EvaluationCriteria.COMMUNICATION]: 4,
    [EvaluationCriteria.EFFORT]: 4,
};

const OffensiveLineWeights = {
    [EvaluationCriteria.SPEED]: 3,
    [EvaluationCriteria.AGILITY]: 4,
    [EvaluationCriteria.STRENGTH]: 20,
    [EvaluationCriteria.ENDURANCE]: 5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 3,
    [EvaluationCriteria.BLOCKING]: 30,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 18,
    [EvaluationCriteria.FOOTBALL_IQ]: 5,
    [EvaluationCriteria.BALL_SECURITY]: 2,
    [EvaluationCriteria.LEADERSHIP]: 4,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 4,
};

const DefensiveLineWeights = {
    [EvaluationCriteria.SPEED]: 5,
    [EvaluationCriteria.AGILITY]: 5,
    [EvaluationCriteria.STRENGTH]: 26,
    [EvaluationCriteria.ENDURANCE]: 4,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 0,
    [EvaluationCriteria.TACKLING]: 25,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 16,
    [EvaluationCriteria.FOOTBALL_IQ]: 4,
    [EvaluationCriteria.BALL_SECURITY]: 2,
    [EvaluationCriteria.LEADERSHIP]: 3,
    [EvaluationCriteria.COMMUNICATION]: 2,
    [EvaluationCriteria.EFFORT]: 8,
};

const LinebackerWeights = {
    [EvaluationCriteria.SPEED]: 8.6,
    [EvaluationCriteria.AGILITY]: 8.6,
    [EvaluationCriteria.STRENGTH]: 13.6,
    [EvaluationCriteria.ENDURANCE]: 4.5,
    [EvaluationCriteria.THROWING_ACCURACY]: 0,
    [EvaluationCriteria.ARM_STRENGTH]: 0,
    [EvaluationCriteria.CATCHING]: 3.4,
    [EvaluationCriteria.TACKLING]: 22.6,
    [EvaluationCriteria.BLOCKING]: 0,
    [EvaluationCriteria.POSITION_TECHNIQUE]: 13.6,
    [EvaluationCriteria.FOOTBALL_IQ]: 5.6,
    [EvaluationCriteria.BALL_SECURITY]: 2.5,
    [EvaluationCriteria.LEADERSHIP]: 5.6,
    [EvaluationCriteria.COMMUNICATION]: 4.5,
    [EvaluationCriteria.EFFORT]: 6.6,
};

export const PlayerRatingWeights = {
    [PlayerPositions.QUARTERBACK]: QuarterbackWeights,
    [PlayerPositions.WIDE_RECEIVER]: WideReceiverWeights,
    [PlayerPositions.RUNNING_BACK]: RunningBackWeights,
    [PlayerPositions.TIGHT_END]: TightEndWeights,
    [PlayerPositions.CENTER]: CenterWeights,
    [PlayerPositions.OFFENSIVE_TACKLE]: OffensiveTackleWeights,
    [PlayerPositions.OFFENSIVE_GUARD]: OffensiveGuardWeights,
    [PlayerPositions.CORNERBACK]: CornerbackWeights,
    [PlayerPositions.SAFETY]: SafetyWeights,
    [PlayerPositions.DEFENSIVE_TACKLE]: DefensiveTackleWeights,
    [PlayerPositions.DEFENSIVE_END]: DefensiveEndWeights,
    [PlayerPositions.OUTSIDE_LINEBACKER]: OutsideLinebackerWeights,
    [PlayerPositions.MIDDLE_LINEBACKER]: MiddleLinebackerWeights,
    [PlayerPositions.KICKER]: KickerWeights,
    [PlayerPositions.PUNTER]: PunterWeights,
    [PlayerPositions.DEFENSIVE_BACK]: DefensiveBackWeights,
    [PlayerPositions.OFFENSIVE_LINEMAN]: OffensiveLineWeights,
    [PlayerPositions.DEFENSIVE_LINEMAN]: DefensiveLineWeights,
    [PlayerPositions.LINEBACKER]: LinebackerWeights,
};

export const PlayTags = ['Offense', 'Defense', 'Press', 'Press Break', 'Out-of-Bounds'];

export const DrillSkillTags = ['Ball Handling', 'Read-and-React', 'Transition'];

export const DepthSections = {
    starters: 'starters',
};

export const DepthConfig: any = {
    [Sports.HighSchoolFootball]: {
        [DepthSections.starters]: {
            name: 'Starters',
            starters: 5,
        },
    },
};

export const DepthChartLayout = {
    [Sports.HighSchoolFootball]: [[DepthSections.starters]],
};

// No variation from roster display names
export const PlayerRoleNames = PlayerPositionNames;

export const TeamSizes = footballlab.TeamSizes;

export const AddonConfig = footballlab.AddonConfig;

export const ScoutingReportStats = {
    GAMES: 'GAMES',
    TOTAL_POINTS: 'TOTAL_POINTS',
    POINTS_PER_GAME: 'POINTS_PER_GAME',
    SCORING_MARGIN: 'SCORING_MARGIN',
    FG_MADE_ATTEMPTED: 'FG_MADE_ATTEMPTED',
    FG_PCT: 'FG_PCT',
    FG_PER_GAME: 'FG_PER_GAME',
    _3PT_MADE_ATTEMPTED: '_3PT_MADE_ATTEMPTED',
    _3PT_PCT: '_3PT_PCT',
    _3PT_PER_GAME: '_3PT_PER_GAME',
    FT_MADE_ATTEMPTED: 'FT_MADE_ATTEMPTED',
    FT_PCT: 'FT_PCT',
    FT_PER_GAME: 'FT_PER_GAME',
    REBOUND_TOTAL: 'REBOUND_TOTAL',
    REBOUND_PER_GAME: 'REBOUND_PER_GAME',
    REBOUND_MARGIN: 'REBOUND_MARGIN',
    ASSISTS_TOTAL: 'ASSISTS_TOTAL',
    ASSISTS_PER_GAME: 'ASSISTS_PER_GAME',
    TURNOVERS_TOTAL: 'TURNOVERS_TOTAL',
    TURNOVERS_PER_GAME: 'TURNOVERS_PER_GAME',
    TURNOVERS_MARGIN: 'TURNOVERS_MARGIN',
    ASSIST_TURNOVER_RATIO: 'ASSIST_TURNOVER_RATIO',
    POINTS_OFF_TURNOVERS: 'POINTS_OFF_TURNOVERS',
};

export const ScoutingReportStatDetails = {
    [ScoutingReportStats.GAMES]: {
        key: ScoutingReportStats.GAMES,
        name: 'Games',
        shortKey: 'GP',
    },
    [ScoutingReportStats.TOTAL_POINTS]: {
        key: ScoutingReportStats.TOTAL_POINTS,
        shortKey: 'PTS',
        name: 'Total Points',
    },
    [ScoutingReportStats.POINTS_PER_GAME]: {
        key: ScoutingReportStats.POINTS_PER_GAME,
        name: 'Points Per Game',
        formula: [ScoutingReportStats.TOTAL_POINTS, '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats.SCORING_MARGIN]: {
        key: ScoutingReportStats.SCORING_MARGIN,
        name: 'Scoring Margin',
    },
    [ScoutingReportStats.FG_MADE_ATTEMPTED]: {
        key: ScoutingReportStats.FG_MADE_ATTEMPTED,
        name: 'FG: Made-Attempted',
        format: '-',
    },
    [ScoutingReportStats.FG_PCT]: {
        key: ScoutingReportStats.FG_PCT,
        name: 'FG: Percentage',
        formula: ['a-b', ScoutingReportStats.FG_MADE_ATTEMPTED, 'a', '/', 'b'],
    },
    [ScoutingReportStats.FG_PER_GAME]: {
        key: ScoutingReportStats.FG_PER_GAME,
        name: 'FG: Per Game',
        formula: ['a-b', ScoutingReportStats.FG_MADE_ATTEMPTED, 'a', '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats._3PT_MADE_ATTEMPTED]: {
        key: ScoutingReportStats._3PT_MADE_ATTEMPTED,
        name: '3PT: Made-Attempted',
        format: '-',
    },
    [ScoutingReportStats._3PT_PCT]: {
        key: ScoutingReportStats._3PT_PCT,
        name: '3PT: Percentage',
        formula: ['a-b', ScoutingReportStats._3PT_MADE_ATTEMPTED, 'a', '/', 'b'],
    },
    [ScoutingReportStats._3PT_PER_GAME]: {
        key: ScoutingReportStats._3PT_PER_GAME,
        name: '3PT: Per Game',
        formula: ['a-b', ScoutingReportStats._3PT_MADE_ATTEMPTED, 'a', '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats.FT_MADE_ATTEMPTED]: {
        key: ScoutingReportStats.FT_MADE_ATTEMPTED,
        name: 'FT: Made-Attempted',
        format: '-',
    },
    [ScoutingReportStats.FT_PCT]: {
        key: ScoutingReportStats.FT_PCT,
        name: 'FT: Percentage',
        formula: ['a-b', ScoutingReportStats.FT_MADE_ATTEMPTED, 'a', '/', 'b'],
    },
    [ScoutingReportStats.FT_PER_GAME]: {
        key: ScoutingReportStats.FT_PER_GAME,
        name: 'FT: Per Game',
        formula: ['a-b', ScoutingReportStats.FT_MADE_ATTEMPTED, 'a', '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats.REBOUND_TOTAL]: {
        key: ScoutingReportStats.REBOUND_TOTAL,
        shortKey: 'RB',
        name: 'Total Rebounds',
    },
    [ScoutingReportStats.REBOUND_PER_GAME]: {
        key: ScoutingReportStats.REBOUND_PER_GAME,
        name: 'Rebounds Per Game',
        formula: [ScoutingReportStats.REBOUND_TOTAL, '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats.REBOUND_MARGIN]: {
        key: ScoutingReportStats.REBOUND_MARGIN,
        name: 'Margin',
    },
    [ScoutingReportStats.ASSISTS_TOTAL]: {
        key: ScoutingReportStats.ASSISTS_TOTAL,
        shortKey: 'AST',
        name: 'Total Assists',
    },
    [ScoutingReportStats.ASSISTS_PER_GAME]: {
        key: ScoutingReportStats.ASSISTS_PER_GAME,
        name: 'Assists Per Game',
        formula: [ScoutingReportStats.ASSISTS_TOTAL, '/', ScoutingReportStats.GAMES],
    },
    [ScoutingReportStats.TURNOVERS_TOTAL]: {
        key: ScoutingReportStats.TURNOVERS_TOTAL,
        shortKey: 'TO',
        name: 'Total Turnovers',
        lowerIsBetter: true,
    },
    [ScoutingReportStats.TURNOVERS_PER_GAME]: {
        key: ScoutingReportStats.TURNOVERS_PER_GAME,
        name: 'Turnovers Per Game',
        formula: [ScoutingReportStats.TURNOVERS_TOTAL, '/', ScoutingReportStats.GAMES],
        lowerIsBetter: true,
    },
    [ScoutingReportStats.TURNOVERS_MARGIN]: {
        key: ScoutingReportStats.TURNOVERS_MARGIN,
        name: 'Margin',
    },
    [ScoutingReportStats.ASSIST_TURNOVER_RATIO]: {
        key: ScoutingReportStats.ASSIST_TURNOVER_RATIO,
        name: 'Assist/Turnover Ratio',
        formula: [ScoutingReportStats.ASSISTS_TOTAL, '/', ScoutingReportStats.TURNOVERS_TOTAL],
    },
    [ScoutingReportStats.POINTS_OFF_TURNOVERS]: {
        key: ScoutingReportStats.POINTS_OFF_TURNOVERS,
        name: 'Points Off Turnovers',
    },
};

const bballScoutingReportStats = [
    {
        categoryName: 'PREVIEW',
        stats: [ScoutingReportStats.GAMES, ScoutingReportStats.TOTAL_POINTS, ScoutingReportStats.POINTS_PER_GAME],
    },
    {
        categoryName: 'Scoring',
        stats: [
            ScoutingReportStats.GAMES,
            ScoutingReportStats.TOTAL_POINTS,
            ScoutingReportStats.POINTS_PER_GAME,
            ScoutingReportStats.SCORING_MARGIN,
        ],
    },
    {
        categoryName: 'Shooting',
        stats: [
            ScoutingReportStats.FG_MADE_ATTEMPTED,
            ScoutingReportStats.FG_PCT,
            ScoutingReportStats.FG_PER_GAME,
            ScoutingReportStats._3PT_MADE_ATTEMPTED,
            ScoutingReportStats._3PT_PCT,
            ScoutingReportStats._3PT_PER_GAME,
            ScoutingReportStats.FT_MADE_ATTEMPTED,
            ScoutingReportStats.FT_PCT,
            ScoutingReportStats.FT_PER_GAME,
        ],
    },
    {
        categoryName: 'Rebounding',
        stats: [
            ScoutingReportStats.REBOUND_TOTAL,
            ScoutingReportStats.REBOUND_PER_GAME,
            ScoutingReportStats.REBOUND_MARGIN,
        ],
    },
    {
        categoryName: 'Assists',
        stats: [ScoutingReportStats.ASSISTS_TOTAL, ScoutingReportStats.ASSISTS_PER_GAME],
    },
    {
        categoryName: 'Turnovers',
        stats: [
            ScoutingReportStats.TURNOVERS_TOTAL,
            ScoutingReportStats.TURNOVERS_PER_GAME,
            ScoutingReportStats.TURNOVERS_MARGIN,
            ScoutingReportStats.ASSIST_TURNOVER_RATIO,
            ScoutingReportStats.POINTS_OFF_TURNOVERS,
        ],
    },
];

export const ScoutingReportStatDisplay = {
    [Sports.HighSchoolFootball]: bballScoutingReportStats,
};

export const PersonnelQuickStats = [
    ScoutingReportStats.GAMES,
    ScoutingReportStats.TOTAL_POINTS,
    ScoutingReportStats.ASSISTS_TOTAL,
    ScoutingReportStats.REBOUND_TOTAL,
    ScoutingReportStats.TURNOVERS_TOTAL,
];

export const Features = footballlab.Features;

const Config: ConfigSpec = {
    SiteConfig,
    GoogleAnalyticsID,
    Sports,
    PlayTags,
    DrillSkillTags,
    SportsNames,
    PlayerPositions,
    PlayerRatingWeights,
    DepthSections,
    DepthConfig,
    DepthChartLayout,
    PlayerPositionNames,
    PlayerRatingSections,
    SportPositions,
    PlayerRoleNames,
    TeamSizes,
    AddonConfig,
    ScoutingReportStats,
    ScoutingReportStatDetails,
    ScoutingReportStatDisplay,
    PersonnelQuickStats,
    Features,
};

export default Config;
